import type { CreatorTagColor } from "@unbox/api";
import { Input } from "@/components/base/input/input";
import { cx } from "@/utils/cx";
import { TagColorPicker } from "./tag-color-picker";

type TagColorNameInputProps = {
  color: string;
  onColorChange: (color: CreatorTagColor) => void;
  name: string;
  onNameChange: (name: string) => void;
  // Used as both the input's placeholder and aria-label, e.g. "Cohort name" / "Tag name".
  label: string;
  /**
   * Fills the container rather than sitting at a fixed field width. The input group
   * centres whatever it holds, so a field narrower than the container is centred
   * rather than left-aligned — filling the width is what keeps the text on the left.
   */
  fullWidth?: boolean;
};

// Color swatch + name input, tinted by the chosen color via color-mix. Shared between
// the tag/cohort library editors and the cohort CSV import preview.
export const TagColorNameInput = ({
  color,
  onColorChange,
  name,
  onNameChange,
  label,
  fullWidth = false,
}: TagColorNameInputProps) => (
  <div
    className={cx("flex items-center gap-2 border-1 rounded-xl p-1 pl-2", fullWidth && "w-full")}
    style={{
      backgroundColor: `color-mix(in srgb, ${color} 20%, transparent)`,
      borderColor: `color-mix(in srgb, ${color} 30%, transparent)`,
    }}
  >
    <TagColorPicker color={color} onChange={onColorChange} />
    <Input
      value={name}
      onChange={onNameChange}
      placeholder={label}
      aria-label={label}
      maxLength={50}
      className={fullWidth ? "min-w-0 flex-1" : undefined}
      inputClassName={cx("text-sm", fullWidth ? "w-full" : "w-72")}
    />
  </div>
);
